import React, { useEffect, useState } from 'react';
import { Badge, Drawer, Row } from 'antd';
import { getService, getSharedComponent } from '@4r/mf-host';
import { ReactComponent as OnlineSVG } from '../../assets/svg/online.svg';
import { ReactComponent as OfflineSVG } from '../../assets/svg/offline.svg';
import { ReactComponent as SyncingSVG } from '../../assets/svg/syncing.svg';
import { ReactComponent as SyncingErrorSVG } from '../../assets/svg/syncing_error.svg';
import { PageStatus } from '../../state/Actions';
import MessageBusEvent from '../../services/MessageBusEvents';

interface OfflineSyncStatus {
	isRunning: boolean;
	isSyncing: boolean;
	pendingCount: number;
	failedCount: number;
	isPaused: boolean;
}

/**
 * Sync indicator in the left menu, as the legacy host had it: online, offline,
 * syncing or failing, and a way into the details.
 *
 * The queues themselves live in work-order (`@4r/common-module-offline-services`),
 * which posts their state here and shares the panel this opens. The panel keeps
 * the queue running while it is open, so a technician can send what is waiting
 * without first opening a work order.
 */
const OnlineStatus = () => {
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [queue, setQueue] = useState<OfflineSyncStatus>();
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	const SyncPanel = getSharedComponent('OfflineSyncPanel') as React.FC | undefined;

	useEffect(() => {
		const onlineHandler = () => setIsOnline(navigator.onLine);

		window.addEventListener('online', onlineHandler);
		window.addEventListener('offline', onlineHandler);

		return function cleanup() {
			window.removeEventListener('online', onlineHandler);
			window.removeEventListener('offline', onlineHandler);
		};
	}, []);

	useEffect(() => {
		const subscription = getService('MessageBusService')?.on(
			MessageBusEvent.OfflineSyncStatus,
			({ detail }: CustomEvent<OfflineSyncStatus | null>) => setQueue(detail ?? undefined),
		);

		return () => subscription?.removeListener();
	}, []);

	let status = PageStatus.online;
	if (!isOnline) status = PageStatus.offline;
	else if (queue?.failedCount) status = PageStatus.error;
	else if (queue?.isSyncing) status = PageStatus.syncing;

	const pending = queue?.pendingCount ?? 0;

	const indicator = {
		[PageStatus.offline]: { icon: <OfflineSVG />, label: 'Offline', iconClass: '', labelClass: ' left-menu__column__status--offline' },
		[PageStatus.syncing]: { icon: <SyncingSVG />, label: 'Syncing', iconClass: ' left-menu__column__status--syncing', labelClass: '' },
		[PageStatus.error]: { icon: <SyncingErrorSVG />, label: 'Not synced', iconClass: '', labelClass: '' },
		[PageStatus.online]: { icon: <OnlineSVG />, label: 'Online', iconClass: '', labelClass: '' },
	}[status];

	const content = (
		<>
			<Row justify="center" className={`left-menu__column__status${indicator.iconClass}`}>
				<Badge count={pending} size="small" offset={[4, 0]}>
					{indicator.icon}
				</Badge>
			</Row>
			<Row justify="center" className={`left-menu__column__status${indicator.labelClass}`}>
				{indicator.label}
			</Row>
		</>
	);

	// Without work-order loaded there is no queue to look into, and no panel to show.
	if (!SyncPanel) {
		return content;
	}

	return (
		<>
			<div
				role="button"
				tabIndex={0}
				className="left-menu__column__status-trigger"
				title="Sync status"
				onClick={() => setIsPanelOpen(true)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') setIsPanelOpen(true);
				}}
			>
				{content}
			</div>
			<Drawer title="Sync status" placement="left" visible={isPanelOpen} onClose={() => setIsPanelOpen(false)} destroyOnClose>
				<SyncPanel />
			</Drawer>
		</>
	);
};

export default OnlineStatus;
